/**
 * SidebarCard - Card compacto padrão (T1) para a sidebar direita do ticket.
 *
 * Header com ícone + título (+ slot opcional à direita) e corpo com padding
 * reduzido — equivalente compacto do `FormSection` shared (que usa `p-6`,
 * grande demais para empilhar 5-6 cards numa aside de 318px). Reusado por
 * `DetailsCard`, `PeopleCard`, `ClientContactCard`, `SlaCard`, `ProjectCard`
 * e `DescriptionCard`.
 */
import React, { useState } from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

export interface SidebarCardProps {
  icon: IconName;
  title: string;
  children: React.ReactNode;
  /** Permite recolher o corpo do card (header sempre visível). */
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Slot extra no header (ex.: link "abrir →", badge). */
  headerRight?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const SidebarCard: React.FC<SidebarCardProps> = ({
  icon,
  title,
  children,
  collapsible = false,
  defaultCollapsed = false,
  headerRight,
  className = '',
  'data-testid': testId,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div
      data-testid={testId}
      className={`rounded-lg border flex-shrink-0 ${className}`}
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 ${collapsible ? 'cursor-pointer select-none' : ''}`}
        style={collapsed ? undefined : { borderBottom: '1px solid var(--theme-card-border)' }}
        onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCollapsed((c) => !c);
                }
              }
            : undefined
        }
      >
        <Icon name={icon} className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
        <span className="text-xs font-semibold flex-1 truncate" style={{ color: 'var(--theme-card-text)' }}>
          {title}
        </span>
        {headerRight}
        {collapsible && (
          <Icon
            name={collapsed ? 'chevron-down' : 'chevron-up'}
            className="w-3.5 h-3.5 flex-shrink-0"
            style={{ color: 'var(--theme-muted-text)' }}
          />
        )}
      </div>
      {!collapsed && <div className="px-3 py-0.5">{children}</div>}
    </div>
  );
};

SidebarCard.displayName = 'SidebarCard';
